import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import LegalPage from "@/pages/LegalPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import AdminLayout from "@/admin/AdminLayout";
import AdminLogin from "@/pages/AdminLogin";
import Activate from "@/pages/Activate";
import SetPassword from "@/pages/SetPassword";
import PortalLayout from "@/portal/PortalLayout";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

const FullLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F8FAF9]">
    <Loader2 className="h-8 w-8 text-[#10B981] animate-spin" />
  </div>
);

function RequireAdmin() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "customer") return <Navigate to="/portal" replace />;
  return <AdminLayout />;
}

function RequirePortal() {
  const { user, loading } = useAuth();
  if (loading) return <FullLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "super_admin" || user.role === "staff") return <Navigate to="/admin" replace />;
  return <PortalLayout />;
}

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    const priv = ["/login", "/admin-login", "/admin", "/portal", "/activate", "/set-password"];
    const isPrivate = priv.some((p) => location.pathname.startsWith(p));
    let tag = document.querySelector('meta[name="robots"]');
    if (!tag) { tag = document.createElement("meta"); tag.name = "robots"; document.head.appendChild(tag); }
    tag.content = isPrivate ? "noindex, nofollow" : "index, follow";
  }, [location.pathname]);
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route path="/activate" element={<Activate />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/legal/:slug" element={<LegalPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/admin/*" element={<RequireAdmin />} />
      <Route path="/portal/*" element={<RequirePortal />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
